import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import WarningIcon from '@mui/icons-material/Warning';

import { loadLocationsByServiceId } from '../../../store/directusService';
import { clearUsersBySearch, loadUsersBySearch } from '../../../store/userService';
import { todayDate } from '../../../utils/dateFunctions';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { MAX_URL } from '../../../utils/formValidation/commonConstants';
import DateSelect from '../../formElements/DateSelect';
import TextInputField from '../../formElements/TextInputField';
import StartAndEndTimesSelect from '../../scheduling/components/StartAndEndTimesSelect';
import FormActions from '../../ui/formActions/FormActions';
import AdviserSelect from '../components/AdviserSelect';
import AppointmentTypeSelect from '../components/AppointmentTypeSelect';
import LocationSelect from '../components/LocationSelect';

import { validate, validateDiff } from './validation/validateInitialAppointment';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import appointment from './styles/appointments.module.css';
import local from './styles/initialAppointment.module.css';

const InitialAppointment = ({
    onClose,
    isDisabled,
    updateInitialAppointment,
    isRequired,
    warnings,
    savedAppointment
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        participantId: '',
        serviceId: '',
        userId: '',
        typeId: '',
        type: '',
        locationId: '',
        location: {},
        remoteCallUrl: '',
        startTime: '08:00',
        endTime: '09:00',
        date: todayDate(),
        mandatory: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [filteredAppointmentTypes, setFilteredAppointmentTypes] = useState([]);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const users = useSelector((state) => state.entities.userService.usersBySearch);
    const usersMetaData = useSelector((state) => state.entities.userService.usersBySearchMetaData);
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const locationsByServiceId = useSelector(
        (state) => state.entities.directusService.locationsByServiceId
    );

    // USEEFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        setNewEntry((prev) => ({ ...prev, userId: loggedInUser.id }));
    }, [loggedInUser]);

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        const { contractId, serviceId } = currentParticipant;

        if (contractId && appointmentTypeDetails?.length && filteredAppointmentTypes.length < 1) {
            setFilteredAppointmentTypes(getConfiguredItems(appointmentTypeDetails, contractId));
        }

        dispatch(loadUsersBySearch({ serviceIds: [serviceId] }, 0));

        if (!(serviceId in locationsByServiceId)) {
            dispatch(loadLocationsByServiceId([serviceId]));
        }
    }, [currentParticipant, appointmentTypeDetails, locationsByServiceId]);

    useEffect(() => {
        if (users?.length > 0 && 'number' in usersMetaData && !usersMetaData?.last) {
            dispatch(
                loadUsersBySearch(
                    { serviceIds: [currentParticipant.serviceId] },
                    usersMetaData.number + 1
                )
            );
        }
    }, [users, usersMetaData]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNs
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const resetFields = () => {
        setNewEntry(initialState);
        setErrors({});
        setIsButtonDisabled(false);
    };

    const cleanUp = () => {
        resetFields();
        dispatch(clearUsersBySearch());
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateAppointmentType = (key, value, type) => {
        onUpdate(key, value);
        setNewEntry((prev) => ({ ...prev, type }));
    };

    const onUpdateTime = (key, value) => {
        clearError(key === 'endTime' ? 'startTime' : 'endTime');
        onUpdate(key, value);
    };

    const onUpdateLocation = (key, value, location) => {
        clearError(key);
        clearError('remoteCallUrl');
        const remoteCallUrl = !location?.remote ? '' : newEntry.remoteCallUrl;
        setNewEntry((prev) => ({ ...prev, location, remoteCallUrl, [key]: value }));
    };

    const onCancel = () => {
        resetFields();
        updateInitialAppointment({});
        if (!warnings.length) onClose('initialAppointment');
    };

    const onUpdateInitialAppointment = () => {
        const newErrors = validate(newEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        /* eslint-disable no-unused-vars */
        const { location, remoteCallUrl, ...rest } = newEntry;
        const payload = {
            ...rest,
            remoteCallUrl: remoteCallUrl ? encodeURI(remoteCallUrl) : null,
            participantId: currentParticipant.id,
            serviceId: currentParticipant.serviceId
        };
        const diff = validateDiff(payload, savedAppointment);
        if (Object.values(diff).some((el) => el.error)) {
            setErrors(diff);
            return;
        }
        updateInitialAppointment(payload);
    };

    return (
        <div className={appointment.componentSection}>
            {isRequired.bookNextAppointment && (
                <>
                    <h2 className={app.sectionHeading}>Book Next Appointment</h2>
                    <form className={form.form} data-testid={'initial_appointment_form'}>
                        <div className={form.formSection}>
                            <div className={form.formColumn}>
                                <AppointmentTypeSelect
                                    appointmentTypes={filteredAppointmentTypes}
                                    appointmentTypeId={newEntry.typeId}
                                    disabled={isDisabled}
                                    onAppointmentTypeChange={onUpdateAppointmentType}
                                    errors={errors}
                                />
                                <LocationSelect
                                    locationId={newEntry.locationId}
                                    serviceId={currentParticipant.serviceId}
                                    errors={errors}
                                    disabled={isDisabled}
                                    onLocationChange={onUpdateLocation}
                                />
                                {newEntry.location?.remote && (
                                    <TextInputField
                                        id={'remoteCallUrl'}
                                        label={'Remote Call URL'}
                                        placeholder={'Enter a remote call URL'}
                                        disabled={isDisabled}
                                        maxLength={MAX_URL}
                                        value={newEntry.remoteCallUrl || ''}
                                        error={errors.remoteCallUrl}
                                        onChange={(e) =>
                                            onUpdate('remoteCallUrl', e.target.value.toLowerCase())
                                        }
                                    />
                                )}
                                <AdviserSelect
                                    userId={newEntry.userId || ''}
                                    serviceId={currentParticipant.serviceId}
                                    initialAdviser={loggedInUser}
                                    onAdviserChange={(id) => onUpdate('userId', id)}
                                    disabled={isDisabled}
                                    mandatory={true}
                                    errors={errors}
                                />
                            </div>
                            <div className={form.formColumn}>
                                <StartAndEndTimesSelect
                                    startTime={newEntry.startTime}
                                    endTime={newEntry.endTime}
                                    disabled={isDisabled}
                                    errors={errors}
                                    onTimeChange={onUpdateTime}
                                />
                                <DateSelect
                                    mandatory={true}
                                    value={newEntry.date}
                                    disabled={isDisabled}
                                    error={errors.date}
                                    onDateChange={(date) => onUpdate('date', date)}
                                />
                            </div>
                        </div>
                        <div className={local.formActions}>
                            <FormActions
                                onClose={onUpdateInitialAppointment}
                                type="button"
                                onCancel={onCancel}
                                btnText="Update"
                                disabled={isButtonDisabled || isDisabled}
                                error={errors.button}
                                clearError={() => clearError('button')}
                            />
                        </div>
                    </form>

                    <div
                        className={appointment.warnings}
                        data-testid="initial_appointment_warnings">
                        {warnings?.length > 0 &&
                            warnings.map((el) => (
                                <div key={el} className={appointment.warning}>
                                    <WarningIcon />
                                    {el}
                                </div>
                            ))}
                    </div>
                </>
            )}
        </div>
    );
};

InitialAppointment.propTypes = {
    onClose: PropTypes.func,
    updateInitialAppointment: PropTypes.func,
    isRequired: PropTypes.object,
    warnings: PropTypes.array,
    savedAppointment: PropTypes.object,
    isDisabled: PropTypes.bool
};

export default InitialAppointment;
